import { generateLeaseKey } from './leaseKeyUtils';

/**
 * Apply filter to search results based on selected lease keys
 * @param {Object} searchResults - Original search results
 * @param {Set<string>} selectedLeaseKeys - Set of lease keys (District-Lease# format) to filter by
 * @returns {Object} Filtered search results
 */
export const applyResultsFilter = (searchResults, selectedLeaseKeys) => {
  // If no filter or no lease keys selected, return original results
  if (!selectedLeaseKeys || selectedLeaseKeys.size === 0 || !searchResults?.data) {
    return searchResults;
  }

  // Build map of lease keys to include (for fast lookup)
  const leaseKeysSet = new Set(selectedLeaseKeys);

  // Filter function for records that can be matched by lease key
  const matchesLeaseKey = (record) => {
    const district = record['District'] || record.district || '00';
    const leaseNumber = record['Lease Number'] || record.lease_number || null;
    const leaseName = record['Lease Name'] || record.lease_name || 
                      record['Lease Facility Name'] || record.lease_facility_name || null;
    
    const leaseKey = generateLeaseKey(district, leaseNumber, leaseName);
    return leaseKeysSet.has(leaseKey);
  };

  // Filter Wells
  const filteredWells = searchResults.data.wells 
    ? searchResults.data.wells.filter(well => matchesLeaseKey(well))
    : [];

  // Build set of well API numbers from filtered wells (for production/inspections/violations matching)
  const filteredWellAPIs = new Set(
    filteredWells.map(well => 
      well['API Number'] || well.api_number || well.api
    ).filter(api => api)
  );

  // For production, inspections, violations, severances, spills:
  // Filter by lease key OR by well API (if they have API number)
  const filterTableRecords = (records) => {
    if (!records || !Array.isArray(records)) return [];
    
    return records.filter(record => {
      // Check by lease key
      if (matchesLeaseKey(record)) return true;
      
      // Check by well API (if record has API number)
      const apiNumber = record['API Number'] || record.api_number || record.api;
      if (apiNumber && filteredWellAPIs.has(apiNumber)) return true;
      
      return false;
    });
  };

  // Filter each table
  const filteredData = {
    wells: filteredWells,
    production: filterTableRecords(searchResults.data.production),
    inspections: filterTableRecords(searchResults.data.inspections),
    violations: filterTableRecords(searchResults.data.violations),
    severances: filterTableRecords(searchResults.data.severances),
    spills: filterTableRecords(searchResults.data.spills),
  };

  // Recalculate counts based on filtered data
  const filteredCounts = {
    wells: filteredData.wells.length,
    production: filteredData.production.length,
    inspections: filteredData.inspections.length,
    violations: filteredData.violations.length,
    severances: filteredData.severances.length,
    spills: filteredData.spills.length,
  };

  return {
    ...searchResults,
    data: filteredData,
    counts: filteredCounts
  };
};

